import React, { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';

import uniqBy from 'lodash/uniqBy';
import { useIntl } from 'react-intl';

import { fg } from '@atlaskit/platform-feature-flags/fg';
import type { ProductKeys3P as ProductKeys3PType } from '@atlassian/rovo-connectors-config/types';
import { SAINFeatureControl } from '@atlassian/search-ai/sain-feature-control';
import { createAtlAttributionHeaderValue } from '@atlassian/search-client/atl-attribution';
import { CancelError, CancellablePromise } from '@atlassian/search-client/cancellable-promise';
import { type Maybe } from '@atlassian/search-client/common/types';
import { type AggError } from '@atlassian/search-client/fetch-agg';
import { type ActivityItemEdge } from '@atlassian/search-client/recent-activities/types';
import { type SearchPageNode } from '@atlassian/search-client/search-page/types';
import { type ZeroQueryType } from '@atlassian/search-client/zero-queries/types';
import { STATSIG_QUICK_FIND_LAYER } from '@atlassian/search-experiment/constants';
import { getMutuallyExclusiveUIExperiment } from '@atlassian/search-experiment/frontend';

import { getUppercasePrimaryProductKey } from '../../../common/constants/get-uppercase-primary-product-key';
import { is3pProductKeyForAnalytics } from '../../../common/constants/is3p-product-key-for-analytics';
import { PrimaryProductKeys } from '../../../common/constants/primary-product-keys';
import { isProductKey, type ProductKeys } from '../../../common/constants/product-keys';
import { SearchExperience } from '../../../common/constants/search-experience';
import type { QuickFindResult } from '../../../common/ui/quick-find/types.tsx';
import { getSessionIdFromLocalStorage } from '../../../common/utils/events/get-session-id-from-local-storage';
import { remapVitalStatsEventForQuickFind } from '../../../common/utils/events/remap-vital-stats-event-for-quick-find';
import { TASK_NAMES } from '../../../common/utils/events/task-names';
import {
	onTaskFail,
	onTaskStart,
	onTaskSuccess,
} from '../../../common/utils/events/vital-stats-events';
import {
	getQuickFindRecs3PSequenceModel,
	triggerExposureForQuickFindInstantSearch,
	serve3PClickIngestionExperiment,
	serveQuickFindInstantSearch,
} from '../../../common/utils/experiments';
import {
	isIntentDetectionTriggeredForExperiment,
	serveJiraNINFpsNLQNavigation,
} from '../../../common/utils/experiments/experiment-exports';
import {
	filterRecentActivities,
	filterSearchResults,
} from '../../../common/utils/filter-search-results';
import { INTENT_DETECTION } from '../../../common/utils/intent-detection';
import { measureAsyncFunction } from '../../../common/utils/measure-duration';
import {
	getIsBulkConnectBannerShown,
	getProductsInRecommendations,
	getProductsInResults,
	getResultsShown,
	getResultTypeCounts,
	getSectionsAndResultsCount,
	getShownZeroQueries,
} from '../../../common/utils/quick-find/analytics';
import { onQuickFindOperational } from '../../../common/utils/quick-find/events/quick-find-operational';
import { onSearchResultsShown } from '../../../common/utils/quick-find/events/search-results-shown';
import { onAllSuggestedQueriesShown } from '../../../common/utils/quick-find/events/suggested-queries-shown';
import { State } from '../../../common/utils/quick-find/state';
import { getZeroQueryVersion, is3PZeroQuery } from '../../../common/utils/quick-find/zero-queries';
import { isResultSmartLink } from '../../../common/utils/third-party-utils';
import { useDebouncedCallback } from '../../../common/utils/use-debounced-callback';
import { mapActivityToSearchResult } from '../../../ui/quick-find/dialog/recent-activity';
import { useLogException } from '../../error-boundary/utils';
import { useSelectedFilters } from '../../filters/selected';
import { useBootstrap, usePrimaryProduct, useSearchSessionId } from '../../store/bootstrap';
import {
	useContent,
	useCurrentQueryFilter,
	useQuickFindActions,
	useQuickFindOpen,
	useQuickFindQuery,
	useSearchErrors,
	useSelectedQueryFilters,
} from '../../store/quick-find';
import {
	useIsSearchReady,
	useRequestedBackendExperiments,
	useRetryCount,
	useSearchActions,
	useSelectedProduct,
	useTypeEntities,
} from '../../store/search';
import {
	getCommonQueryAttributes,
	useQuickFindAnalytics,
	useQuickFindAttributes,
} from '../analytics';
import { getActionSubjectId } from '../get-action-subject-id';
import { type TaskState } from '../quickfind-post-query-vitalstats-context';
import { useQuickFindPostQueryNetworkVitalStats } from '../use-quickfind-post-query-network-vitalstats';

import { fetchRecentActivities } from './activities';
import { dedupAndSortRecentActivities } from './activities/utils';
import { useQuickFindDebounceTimeout, useQuickFindResultsCallbacks } from './callbacks';
import { fetchPeopleAndTeamsResults } from './people-and-teams';
import {
	fetchAssetsEntities,
	fetchAtlasEntities,
	fetchConfluenceAnd3PEntities,
	fetchConfluenceSpaces,
	fetchJiraEntities,
	fetchJiraIssuesAndTypeEntities,
	fetchLoomEntities,
	fetchMercuryEntities,
	fetchTalentEntities,
} from './product-entities';
import { getRecentQueries } from './recent-queries';
import { fetchRecommendedDocuments } from './recommended-documents';
import {
	containsSearchParameters,
	getQuickFindContent,
	type PeopleAndTeams,
	type RecentActivities,
	type RecentQueries,
	type RecommendedDocuments,
	type SearchResults,
	shouldApplyPostQueryResponse,
	shouldClearPostQueryLoading,
	type ZeroQueries,
	type ZeroQuery,
} from './types';
import { fetchZeroQueries, fetchZeroQueriesV1 } from './zero-queries';

const EMPTY_RESULTS = {
	result: [],
	duration: 0,
};

type ResultCallbacks = {
	onPreQueryStart?: () => void;
	onPreQuerySuccess?: () => void;
	onPreQueryFailure?: (error?: Error) => void;
	onPostQueryStart?: () => void;
	onPostQuerySuccess?: (
		duration: number,
		hasThirdPartyResults: boolean,
		resultCount?: number,
	) => void;
	onPostQueryFailure?: (error?: Error) => void;
	onPostQueryCancel?: () => void;
	/** Task state ref for post-query UX task - provides the taskId for network calls */
	postQueryTaskState?: React.MutableRefObject<TaskState>;
	/** Task ID ref for pre-query UX task - provides the taskId for warmup network calls */
	preQueryTaskIdRef?: React.MutableRefObject<string | null>;
};

type UseQuickFindResultsOptions = {
	postQueryDisabled?: boolean;
};

/**
 * Fetches search results using search parameters from the query
 * and filter stores. During and after requests, the results store is
 * updated accordingly. If multiple requests are fired only the last
 * request will cause the results store to be updated.
 */
export const useQuickFindResults: ({
	postQueryDisabled,
	onPreQueryStart,
	onPreQuerySuccess,
	onPreQueryFailure,
	onPostQueryStart,
	onPostQuerySuccess,
	onPostQueryFailure,
	onPostQueryCancel,
	postQueryTaskState,
	preQueryTaskIdRef,
}: UseQuickFindResultsOptions & ResultCallbacks) => import('./types').QuickFindContent = ({
	postQueryDisabled,
	onPreQueryStart,
	onPreQuerySuccess,
	onPreQueryFailure,
	onPostQueryStart,
	onPostQuerySuccess,
	onPostQueryFailure,
	onPostQueryCancel,
	postQueryTaskState,
	preQueryTaskIdRef,
}: UseQuickFindResultsOptions & ResultCallbacks) => {
	const [bootstrap] = useBootstrap();
	// eslint-disable-next-line @atlassian/search-common/no-use-rss-hooks
	const isSearchReady = useIsSearchReady();
	const [searchSessionId] = useSearchSessionId();
	const [selectedProduct] = useSelectedProduct();
	const sessionId = getSessionIdFromLocalStorage();

	const logException = useLogException();
	// eslint-disable-next-line @atlassian/search-common/no-use-rss-hooks
	const [retryCount] = useRetryCount();
	const quickFindOpen = useQuickFindOpen();
	const quickFindQuery = useQuickFindQuery();
	const currentQueryFilter = useCurrentQueryFilter();
	const selectedQueryFilters = useSelectedQueryFilters();
	const { setRequestedBackendExperiments } = useSearchActions();

	const { selectedFilters, entities, hasFiltersSelected } = useSelectedFilters({
		experience: SearchExperience.QUICK_FIND,
	});
	const useTypeEntitiesArgs = useMemo(
		() => ({
			thirdPartyConfigs: bootstrap.thirdPartyConfigs,
		}),
		[bootstrap.thirdPartyConfigs],
	);
	const typeFilterEntities = useTypeEntities(useTypeEntitiesArgs);
	const { fireAnalyticsEvent } = useQuickFindAnalytics();
	const debounceTimeout = useQuickFindDebounceTimeout();
	const { createNetworkCallbacks } = useQuickFindPostQueryNetworkVitalStats();

	/**
	 * Unfortunately, we need to reintroduce this to avoid unnecessarily
	 * fetching results for the same query and filters.
	 *
	 * In order to understand why this is needed, we must first understand that
	 * the filters object is one gigantic object - whenver any kind of updates
	 * happens to the filters, the object reference changes, as it should. This
	 * includes when the loaded options in the filters change, e.g. when opening
	 * the space filter or typing into the lookup query for the filter. These
	 * user interactions cause the filters object to change, which in turn causes
	 * the references of selectedFilters and entities to change, regardless of
	 * whether or not search parameters (query, filters and entities) have changed.
	 *
	 * In the past, we were able to get away wih reacting on references changes
	 * of selectedFilters and entities as it was impossible for filters to change
	 * in Quick Find without actually changing the search parameters. Now that
	 * there are UI elements in Quick Find (e.g. space filter), this is no longer
	 * true.
	 *
	 * This is temporary until we move to search requests being triggered on user
	 * interactions, rather than state updates, which will happen as part of Relay
	 * migration.
	 */
	const selectedFiltersEquality = JSON.stringify(selectedFilters);
	const entitiesEquality = JSON.stringify(entities);

	const prevQueryRef = useRef<string>();
	const prevQuickFindOpenRef = useRef<boolean>(quickFindOpen);
	const onPreQuerySuccessWasFired = useRef<boolean>(false);
	const prevSelectedFiltersEquality = useRef<string>();
	const prevEntitiesEquality = useRef<string>();
	const prevHasFiltersSelected = useRef<boolean>(hasFiltersSelected);
	const fetchRecentActivitiesPromiseRef = useRef<Promise<RecentActivities>>();
	const fetchRecommendedDocumentsPromiseRef = useRef<Promise<RecommendedDocuments>>();
	const preQueryPromiseRef = useRef<CancellablePromise<[RecentActivities, RecommendedDocuments]>>();
	const postQueryPromiseRef = useRef<CancellablePromise<any>>();
	// rovo_qf_reuse_cancelled_requests. No-exposure read (safe on every render); the
	// exposure is fired separately, once, on the Quick Find open transition below.
	const instantSearch = serveQuickFindInstantSearch();
	// Monotonic guard: a response is applied only if its generation is >= the latest
	// applied, so out-of-order (older) responses are dropped. Inert under control
	// (generations never advance).
	const postQueryGenerationRef = useRef(0);
	const latestAppliedPostQueryGenerationRef = useRef(0);
	const prevSelectedProduct = useRef<ProductKeys | undefined>(selectedProduct);

	// Fire the instant-search layer exposure once, when Quick Find is opened — not on
	// every render that reads the (no-exposure) flag. Statsig dedupes per allocation, so
	// this counts a user as exposed at the moment they enter the QF experience.
	const hasFiredInstantSearchExposureRef = useRef(false);
	useEffect(() => {
		if (quickFindOpen && !hasFiredInstantSearchExposureRef.current) {
			hasFiredInstantSearchExposureRef.current = true;
			triggerExposureForQuickFindInstantSearch();
		}
	}, [quickFindOpen]);

	const {
		setUpdateRecentQueries,
		setFetchEntitiesRequestId,
		setFetchEntitiesTraceId,
		setUpdateZeroQueries,
		setFetchSpacesRequestId,
		setSearchErrors,
	} = useQuickFindActions();

	const [preQueryLoading, setPreQueryLoading] = useState(true);
	const [preQueryError, setPreQueryError] = useState<Error | undefined>();

	const [postQueryLoading, setPostQueryLoading] = useState(false);
	const [postQueryError, setPostQueryError] = useState<Error | undefined>();

	const [recentQueries, setRecentQueries] = useState<RecentQueries>([]);
	const [filteredRecentQueries, setFilteredRecentQueries] = useState<RecentQueries>([]);
	const [filteredRecentActivities, setFilteredRecentActivities] =
		useState<RecentActivities>(EMPTY_RESULTS);
	const [filteredRecommendedDocuments, setFilteredRecommendedDocuments] =
		useState<RecommendedDocuments>(EMPTY_RESULTS);
	const [filteredZeroQueries, setFilteredZeroQueries] = useState<ZeroQuery[]>([]);
	const [zeroQueries, setZeroQueries] = useState<ZeroQueries>(EMPTY_RESULTS);
	const [searchResults, setSearchResults] = useState<SearchResults>(EMPTY_RESULTS);
	const [peopleAndTeams, setPeopleAndTeams] = useState<PeopleAndTeams>(EMPTY_RESULTS);

	const [autocompleteSuggestions, setAutocompleteSuggestions] = useState<string[] | undefined>(
		undefined,
	);

	const isConfluence = bootstrap.primaryProduct === PrimaryProductKeys.Confluence;

	const serveZeroQuery =
		((bootstrap.primaryProduct === PrimaryProductKeys.Confluence ||
			bootstrap.primaryProduct === PrimaryProductKeys.Atlas ||
			(bootstrap.primaryProduct === PrimaryProductKeys.RovoExtension &&
				fg('quickfind-rovo-extension'))) &&
			fg('kd_zero_query_in_qf_fg')) ||
		bootstrap.primaryProduct === PrimaryProductKeys.Jira;

	const workspaceId =
		bootstrap.primaryProduct && bootstrap.cloudId && bootstrap.analyticsAttributes?.activationId
			? `ari:cloud:${bootstrap.primaryProduct}:${bootstrap.cloudId}:workspace/${bootstrap.analyticsAttributes.activationId}`
			: '';

	const useKdZeroQueryAtlHeader = fg('kd_zero_query_agg_atl_attribution_header');
	const atlAttributionHeaderValue = useMemo(
		() =>
			createAtlAttributionHeaderValue({
				atlWorkspaceId: workspaceId,
				tenantId: bootstrap.cloudId || '',
				product: bootstrap.primaryProduct || '',
			}),
		[bootstrap.cloudId, bootstrap.primaryProduct, workspaceId],
	);

	/**
	 * Callback to update recent queries for the current product and user
	 */
	const fetchRecentQueries = useCallback(() => {
		if (!bootstrap.primaryProduct || !bootstrap.user?.id) {
			return;
		}

		const recentQueries = getRecentQueries(bootstrap.primaryProduct, bootstrap.user.id);
		setRecentQueries(recentQueries);
	}, [bootstrap.primaryProduct, bootstrap.user?.id, setRecentQueries]);

	/**
	 * Store the callback to update recent queries in the store. This is used to refresh recent queries
	 * when they are updated, e.g. when a search request is made or when a recent query is removed.
	 */
	useEffect(() => {
		fetchRecentQueries();
		setUpdateRecentQueries(fetchRecentQueries);
	}, [fetchRecentQueries, setUpdateRecentQueries]);

	/**
	 * Callback to fetch recent activities
	 */
	const fetchRecentActivitiesCallback = useCallback(
		async (cursor?: string) => {
			if (!bootstrap.cloudId) {
				return [];
			}

			const headers = { 'atl-attribution': atlAttributionHeaderValue };

			const taskId = crypto.randomUUID();
			const taskStartAt = performance.now();
			const baseVitalStatsParams = {
				searchSessionId,
				taskId,
				task: TASK_NAMES.QUICK_FIND_RECENT_ACTIVITIES_UX,
			};

			fireAnalyticsEvent(remapVitalStatsEventForQuickFind(onTaskStart(baseVitalStatsParams)));

			const networkCallbacks = createNetworkCallbacks({
				taskName: TASK_NAMES.QUICK_FIND_RECENT_ACTIVITIES_NETWORK,
				uxTaskId: taskId,
				isPreQuery: true,
			});

			try {
				const recentActivities = await fetchRecentActivities(
					bootstrap.cloudId,
					bootstrap.aggAbsoluteUrl,
					cursor,
					bootstrap.thirdPartyConfigs,
					headers,
					bootstrap.primaryProduct,
					networkCallbacks,
					bootstrap.products,
				);

				const resultsWithFailedHydration = recentActivities
					.filter(
						(activity: QuickFindResult<ActivityItemEdge>) =>
							activity.data?.node?.object?.data === null,
					)
					.map((activity: QuickFindResult<ActivityItemEdge>) => activity.id);

				// It doesn't distinguish between a network call and fetching from cache.
				// We can consolidate that once quick_find_autorefresh_experiment_v3 is shipped.
				fireAnalyticsEvent(
					remapVitalStatsEventForQuickFind(
						onTaskSuccess({
							...baseVitalStatsParams,
							moreContext: {
								timeTaken: performance.now() - taskStartAt,
								resultsLoaded: recentActivities.length,
								resultsWithFailedHydration,
								...(fg('rovo_qf_failed_hydration_result_count') && {
									failedHydrationResultCount: resultsWithFailedHydration.length,
								}),
							},
						}),
					),
				);
				return recentActivities;
			} catch (error) {
				fireAnalyticsEvent(
					remapVitalStatsEventForQuickFind(
						onTaskFail({
							...baseVitalStatsParams,
							moreContext: {
								error: error instanceof Error ? error.message : String(error),
								timeTaken: performance.now() - taskStartAt,
							},
						}),
					),
				);
				throw error;
			}
		},
		[
			bootstrap.cloudId,
			bootstrap.aggAbsoluteUrl,
			bootstrap.thirdPartyConfigs,
			bootstrap.products,
			searchSessionId,
			fireAnalyticsEvent,
			atlAttributionHeaderValue,
			bootstrap.primaryProduct,
			createNetworkCallbacks,
		],
	);

	const fetchRecommendedDocumentsCallback = useCallback(async () => {
		if (bootstrap.cloudId && bootstrap.user?.id && isConfluence) {
			const headers = { 'atl-attribution': atlAttributionHeaderValue };

			// Fire `quick_find_recs_3p_sequence_model_exp` exposure at the
			// exact point we're about to fetch recommended documents — i.e. for
			// users who would actually receive the treatment (or control). This
			// keeps the exposure population aligned with where the model is
			// delivered (Confluence primary product, authenticated user, with at
			// least one Google Drive or SharePoint connector).
			// `FeatureGates.getExperimentValue` dedupes repeated exposures for
			// the same user/experiment, so calling this on every fetch is safe.
			//
			// The default model is the control. The experiment test cohort routes
			// eligible users to the challenger model.
			const featureServiceModel = getQuickFindRecs3PSequenceModel(bootstrap.thirdPartyConfigs, {
				fireExperimentExposure: true,
			});

			if (featureServiceModel) {
				return fetchRecommendedDocuments(
					bootstrap.cloudId,
					bootstrap.user.id,
					bootstrap.aggAbsoluteUrl,
					headers,
					featureServiceModel,
				);
			}

			return fetchRecommendedDocuments(
				bootstrap.cloudId,
				bootstrap.user.id,
				bootstrap.aggAbsoluteUrl,
				headers,
			);
		} else {
			return [];
		}
	}, [
		bootstrap.cloudId,
		bootstrap.user?.id,
		bootstrap.aggAbsoluteUrl,
		bootstrap.thirdPartyConfigs,
		atlAttributionHeaderValue,
		isConfluence,
	]);

	/**
	 * Callback to fetch zero queries
	 */
	const fetchZeroQueriesCallback = useCallback(() => {
		if (!bootstrap.cloudId) {
			return;
		}
		const fetchFn = SAINFeatureControl.isZeroQuerySuggestionsV1Enabled(bootstrap.primaryProduct)
			? fetchZeroQueriesV1
			: fetchZeroQueries;
		measureAsyncFunction(() =>
			fetchFn(
				{
					cloudId: bootstrap.cloudId!,
					product: getUppercasePrimaryProductKey(bootstrap.primaryProduct),
				},
				useKdZeroQueryAtlHeader && atlAttributionHeaderValue
					? { 'atl-attribution': atlAttributionHeaderValue }
					: {},
			),
		)
			.then((result) => {
				setZeroQueries(result ?? EMPTY_RESULTS);
			})
			.catch((error: Error) => {
				logException(error);
				setZeroQueries(EMPTY_RESULTS);
			});
	}, [
		bootstrap.cloudId,
		logException,
		bootstrap.primaryProduct,
		atlAttributionHeaderValue,
		useKdZeroQueryAtlHeader,
	]);

	/**
	 * Store the callback to update zero queries in the store. This is used to refresh zero queries
	 * when they are updated, e.g. when a zero query is clicked.
	 */
	useEffect(() => {
		if (serveZeroQuery) {
			fetchZeroQueriesCallback();
		}
		setUpdateZeroQueries(fetchZeroQueriesCallback);
	}, [fetchZeroQueriesCallback, setUpdateZeroQueries, serveZeroQuery]);

	// Clears the highlighted pre-query collections (recent queries/activities/zero-queries/
	// recommendations) so a previous query's highlightedTitle markup does not linger on the
	// landing view. Shared by the filter-hide path and the instant-search full-clear path.
	const clearFilteredPreQueryResults = useCallback(() => {
		setFilteredRecentQueries([]);
		setFilteredRecentActivities(EMPTY_RESULTS);
		setFilteredZeroQueries([]);
		isConfluence && setFilteredRecommendedDocuments(EMPTY_RESULTS);
	}, [isConfluence]);

	/**
	 * Callback to fetch pre-query results and updates the store with the results
	 */
	const fetchQuickFindPreQuery = useCallback(
		(
			query: string,
			hideFilteredPreQueryResults: boolean,
			shouldRefetchPreQueryResults: boolean,
		) => {
			if (hideFilteredPreQueryResults) {
				clearFilteredPreQueryResults();
			}

			if (
				!fetchRecentActivitiesPromiseRef.current ||
				!fetchRecommendedDocumentsPromiseRef.current
			) {
				setPreQueryError(undefined);
				setPreQueryLoading(true);
				onPreQueryStart?.();
			}

			if (!fetchRecentActivitiesPromiseRef.current) {
				const fetchRecentActivitiesPromise = measureAsyncFunction(
					fetchRecentActivitiesCallback,
				).catch((error: Error) => {
					logException(error);
					setPreQueryError(error);
					onPreQueryFailure?.(error);

					fetchRecentActivitiesPromiseRef.current = undefined;

					return EMPTY_RESULTS;
				});

				fetchRecentActivitiesPromiseRef.current = fetchRecentActivitiesPromise;
			}

			if (!fetchRecommendedDocumentsPromiseRef.current) {
				if (isConfluence) {
					const fetchRecommendedDocumentsPromise = measureAsyncFunction(
						fetchRecommendedDocumentsCallback,
					).catch((error: Error) => {
						logException(error);
						onPreQueryFailure?.(error);

						fetchRecommendedDocumentsPromiseRef.current = undefined;

						return EMPTY_RESULTS;
					});

					fetchRecommendedDocumentsPromiseRef.current = fetchRecommendedDocumentsPromise;
				} else {
					fetchRecommendedDocumentsPromiseRef.current = Promise.resolve(EMPTY_RESULTS);
				}
			}

			if (shouldRefetchPreQueryResults) {
				// mutual exclusivity with postQuery tasks
				measureAsyncFunction(() => fetchRecentActivitiesCallback())
					.then((results) => {
						setPreQueryError(undefined);

						fetchRecentActivitiesPromiseRef.current = Promise.all([
							fetchRecentActivitiesPromiseRef.current,
							Promise.resolve(results),
						]).then(([existingResults, newResults]) => {
							const deduplicatedResults = dedupAndSortRecentActivities(
								existingResults?.result ?? [],
								newResults?.result ?? [],
							);

							return {
								result: deduplicatedResults,
								duration: newResults.duration,
							};
						});
					})
					.catch((error: Error) => {
						logException(error);
						onPreQueryFailure?.(error);
					});

				if (isConfluence) {
					measureAsyncFunction(() => fetchRecommendedDocumentsCallback())
						.then((results) => {
							fetchRecommendedDocumentsPromiseRef.current = Promise.all([
								fetchRecommendedDocumentsPromiseRef.current,
								Promise.resolve(results),
							]).then(([existingResults, newResults]) => {
								const result = uniqBy(
									[...(newResults?.result ?? []), ...(existingResults?.result ?? [])],
									(item) => item.id,
								);

								return {
									result,
									duration: newResults.duration,
								};
							});
						})
						.catch((error: Error) => {
							logException(error);
							onPreQueryFailure?.(error);
						});
				}
			}

			const preQueryPromise = new CancellablePromise(
				Promise.all([
					fetchRecentActivitiesPromiseRef.current,
					fetchRecommendedDocumentsPromiseRef.current,
				]),
			);

			preQueryPromiseRef.current = preQueryPromise;

			preQueryPromise
				.promise()
				.then(([recentActivities, recommendedDocuments]) => {
					if (!onPreQuerySuccessWasFired.current) {
						onPreQuerySuccess?.();
						onPreQuerySuccessWasFired.current = true;
					}

					setPreQueryLoading(false);

					if (hideFilteredPreQueryResults) {
						return;
					}

					const filteredRecentQueries = filterSearchResults(recentQueries, query);
					setFilteredRecentQueries(filteredRecentQueries);

					if (bootstrap.primaryProduct === PrimaryProductKeys.Jira) {
						// We only filter zero queries for Jira as it's needed in post-query state
						const filteredZeroQueries = filterSearchResults(zeroQueries.result, query);
						setFilteredZeroQueries(filteredZeroQueries);
					} else {
						setFilteredZeroQueries(zeroQueries.result);
					}

					const filteredRecentActivityResults = filterRecentActivities(
						recentActivities.result,
						query,
					);
					setFilteredRecentActivities({
						result: filteredRecentActivityResults,
						duration: recentActivities.duration,
					});

					if (isConfluence) {
						const filteredRecommendedDocuments = filterSearchResults(
							recommendedDocuments.result,
							query,
						);
						setFilteredRecommendedDocuments({
							result: filteredRecommendedDocuments,
							duration: recommendedDocuments.duration,
						});
					}
				})
				.catch(() => {
					// no-op on cancel
				})
				.finally(() => {
					preQueryPromiseRef.current = undefined;
				});
		},
		[
			clearFilteredPreQueryResults,
			fetchRecentActivitiesCallback,
			fetchRecommendedDocumentsCallback,
			logException,
			onPreQueryFailure,
			onPreQueryStart,
			onPreQuerySuccess,
			recentQueries,
			zeroQueries,
			isConfluence,
			bootstrap.primaryProduct,
		],
	);

	/**
	 * Callback to fetch confluence entities
	 * @param uxTaskId - The UX task ID to associate with the network requests
	 * @param isPreQuery - Whether this is a pre-query (warmup) request. Defaults to false.
	 */
	const fetchConfluenceEntitiesCallback = useCallback(
		async (uxTaskId: string, isPreQuery: boolean = false) => {
			return Promise.all([
				fetchConfluenceAnd3PEntities(
					bootstrap.aggAbsoluteUrl,
					quickFindQuery,
					bootstrap.primaryProduct,
					bootstrap.cloudId || '',
					searchSessionId,
					selectedFilters,
					bootstrap.experimentConfig,
					bootstrap.productEdition,
					bootstrap.productDataRegions,
					setRequestedBackendExperiments,
					entities,
					bootstrap.thirdPartyConfigs,
					sessionId,
					createNetworkCallbacks({
						taskName: TASK_NAMES.QUICK_FIND_CONFLUENCE_ENTITIES_NETWORK,
						uxTaskId,
						isPreQuery,
					}),
					bootstrap.analyticsAttributes?.activationId,
				),
				fetchConfluenceSpaces(
					bootstrap.aggAbsoluteUrl,
					quickFindQuery,
					bootstrap.primaryProduct,
					bootstrap.cloudId || '',
					searchSessionId,
					selectedFilters,
					bootstrap.experimentConfig,
					bootstrap.productEdition,
					bootstrap.productDataRegions,
					bootstrap.thirdPartyConfigs,
					sessionId,
					createNetworkCallbacks({
						taskName: TASK_NAMES.QUICK_FIND_CONFLUENCE_SPACES_NETWORK,
						uxTaskId,
						isPreQuery,
					}),
					bootstrap.analyticsAttributes?.activationId,
				),
			]).then((results) => {
				const [entities, spaces] = results;
				return {
					results: [...entities.fetchResults, ...spaces.fetchResults],
					fetchEntitiesRequestId: entities.fetchEntitiesRequestId ?? undefined,
					fetchEntitiesTraceId: entities.fetchEntitiesTraceId ?? undefined,
					fetchSpacesRequestId: spaces.fetchSpacesRequestId ?? undefined,
					errors: [...(entities.errors || []), ...(spaces.errors || [])],
					autocompleteSuggestions: entities.autocompleteSuggestions,
				};
			});
		},
		[
			bootstrap.aggAbsoluteUrl,
			bootstrap.primaryProduct,
			bootstrap.cloudId,
			bootstrap.experimentConfig,
			bootstrap.productEdition,
			bootstrap.productDataRegions,
			bootstrap.thirdPartyConfigs,
			bootstrap.analyticsAttributes?.activationId,
			quickFindQuery,
			searchSessionId,
			selectedFilters,
			setRequestedBackendExperiments,
			entities,
			sessionId,
			createNetworkCallbacks,
		],
	);

	const fetchLoomEntitiesCallback = useCallback(
		async (uxTaskId: string, isPreQuery: boolean = false) => {
			return fetchLoomEntities(
				bootstrap.aggAbsoluteUrl,
				quickFindQuery,
				bootstrap.cloudId || '',
				searchSessionId,
				bootstrap.experimentConfig,
				bootstrap.productEdition,
				bootstrap.productDataRegions,
				setRequestedBackendExperiments,
				bootstrap.thirdPartyConfigs,
				sessionId,
				createNetworkCallbacks({
					taskName: TASK_NAMES.QUICK_FIND_LOOM_ENTITIES_NETWORK,
					uxTaskId,
					isPreQuery,
				}),
				bootstrap.analyticsAttributes?.activationId,
			).then((results) => {
				const { fetchResults, fetchEntitiesRequestId, fetchEntitiesTraceId, errors } = results;
				return {
					results: fetchResults,
					fetchEntitiesRequestId: fetchEntitiesRequestId ?? undefined,
					fetchEntitiesTraceId: fetchEntitiesTraceId ?? undefined,
					fetchSpacesRequestId: undefined,
					errors,
					autocompleteSuggestions: undefined,
				};
			});
		},
		[
			bootstrap.aggAbsoluteUrl,
			bootstrap.cloudId,
			bootstrap.experimentConfig,
			bootstrap.productEdition,
			bootstrap.productDataRegions,
			bootstrap.thirdPartyConfigs,
			bootstrap.analyticsAttributes?.activationId,
			quickFindQuery,
			searchSessionId,
			setRequestedBackendExperiments,
			sessionId,
			createNetworkCallbacks,
		],
	);

	/**
	 * Callback to fetch atlas entities
	 */
	const fetchAtlasEntitiesCallback = useCallback(
		async (uxTaskId: string, isPreQuery: boolean = false) => {
			return fetchAtlasEntities(
				bootstrap.aggAbsoluteUrl,
				quickFindQuery,
				bootstrap.primaryProduct,
				bootstrap.cloudId || '',
				searchSessionId,
				selectedFilters,
				bootstrap.experimentConfig,
				bootstrap.productEdition,
				bootstrap.productDataRegions,
				setRequestedBackendExperiments,
				bootstrap.thirdPartyConfigs,
				sessionId,
				createNetworkCallbacks({
					taskName: TASK_NAMES.QUICK_FIND_ATLAS_ENTITIES_NETWORK,
					uxTaskId,
					isPreQuery,
				}),
				bootstrap.analyticsAttributes?.activationId,
			).then((results) => {
				const { fetchResults, fetchEntitiesRequestId, fetchEntitiesTraceId, errors } = results;
				return {
					results: fetchResults,
					fetchEntitiesRequestId: fetchEntitiesRequestId ?? undefined,
					fetchEntitiesTraceId: fetchEntitiesTraceId ?? undefined,
					fetchSpacesRequestId: undefined,
					errors,
					autocompleteSuggestions: undefined,
				};
			});
		},
		[
			bootstrap.aggAbsoluteUrl,
			bootstrap.primaryProduct,
			bootstrap.cloudId,
			bootstrap.experimentConfig,
			bootstrap.productEdition,
			bootstrap.productDataRegions,
			bootstrap.thirdPartyConfigs,
			bootstrap.analyticsAttributes?.activationId,
			quickFindQuery,
			searchSessionId,
			selectedFilters,
			setRequestedBackendExperiments,
			sessionId,
			createNetworkCallbacks,
		],
	);

	const fetchAssetsEntitiesCallback = useCallback(
		async (uxTaskId: string, isPreQuery: boolean = false) => {
			return fetchAssetsEntities(
				bootstrap.aggAbsoluteUrl,
				quickFindQuery,
				bootstrap.primaryProduct,
				bootstrap.cloudId || '',
				searchSessionId,
				selectedFilters,
				bootstrap.experimentConfig,
				bootstrap.productEdition,
				bootstrap.productDataRegions,
				setRequestedBackendExperiments,
				bootstrap.thirdPartyConfigs,
				sessionId,
				createNetworkCallbacks({
					taskName: TASK_NAMES.QUICK_FIND_ASSETS_ENTITIES_NETWORK,
					uxTaskId,
					isPreQuery,
				}),
				bootstrap.analyticsAttributes?.activationId,
			).then((results) => {
				const { fetchResults, fetchEntitiesRequestId, fetchEntitiesTraceId, errors } = results;
				return {
					results: fetchResults,
					fetchEntitiesRequestId: fetchEntitiesRequestId ?? undefined,
					fetchEntitiesTraceId: fetchEntitiesTraceId ?? undefined,
					fetchSpacesRequestId: undefined as string | undefined,
					errors,
					autocompleteSuggestions: undefined,
				};
			});
		},
		[
			bootstrap.aggAbsoluteUrl,
			bootstrap.primaryProduct,
			bootstrap.cloudId,
			bootstrap.experimentConfig,
			bootstrap.productEdition,
			bootstrap.productDataRegions,
			bootstrap.thirdPartyConfigs,
			bootstrap.analyticsAttributes?.activationId,
			quickFindQuery,
			searchSessionId,
			selectedFilters,
			setRequestedBackendExperiments,
			sessionId,
			createNetworkCallbacks,
		],
	);

	const fetchMercuryEntitiesCallback = useCallback(
		async (uxTaskId: string, isPreQuery: boolean = false) => {
			return fetchMercuryEntities(
				bootstrap.aggAbsoluteUrl,
				quickFindQuery,
				bootstrap.primaryProduct,
				bootstrap.cloudId || '',
				searchSessionId,
				selectedFilters,
				bootstrap.experimentConfig,
				bootstrap.productEdition,
				bootstrap.productDataRegions,
				setRequestedBackendExperiments,
				bootstrap.thirdPartyConfigs,
				sessionId,
				createNetworkCallbacks({
					taskName: TASK_NAMES.QUICK_FIND_MERCURY_ENTITIES_NETWORK,
					uxTaskId,
					isPreQuery,
				}),
				bootstrap.analyticsAttributes?.activationId,
			).then((results) => {
				const { fetchResults, fetchEntitiesRequestId, fetchEntitiesTraceId, errors } = results;
				return {
					results: [...fetchResults],
					fetchEntitiesRequestId: fetchEntitiesRequestId ?? undefined,
					fetchEntitiesTraceId: fetchEntitiesTraceId ?? undefined,
					fetchSpacesRequestId: undefined as string | undefined,
					errors,
					autocompleteSuggestions: undefined,
				};
			});
		},
		[
			bootstrap.aggAbsoluteUrl,
			bootstrap.primaryProduct,
			bootstrap.cloudId,
			bootstrap.experimentConfig,
			bootstrap.productEdition,
			bootstrap.productDataRegions,
			bootstrap.thirdPartyConfigs,
			bootstrap.analyticsAttributes?.activationId,
			quickFindQuery,
			searchSessionId,
			selectedFilters,
			setRequestedBackendExperiments,
			sessionId,
			createNetworkCallbacks,
		],
	);

	const fetchTalentEntitiesCallback = useCallback(
		async (uxTaskId: string, isPreQuery: boolean = false) => {
			return fetchTalentEntities(
				bootstrap.aggAbsoluteUrl,
				quickFindQuery,
				bootstrap.primaryProduct,
				bootstrap.cloudId || '',
				searchSessionId,
				selectedFilters,
				bootstrap.experimentConfig,
				bootstrap.productEdition,
				bootstrap.productDataRegions,
				setRequestedBackendExperiments,
				bootstrap.thirdPartyConfigs,
				sessionId,
				createNetworkCallbacks({
					taskName: TASK_NAMES.QUICK_FIND_TALENT_ENTITIES_NETWORK,
					uxTaskId,
					isPreQuery,
				}),
				bootstrap.analyticsAttributes?.activationId,
			).then((results) => {
				const { fetchResults, fetchEntitiesRequestId, fetchEntitiesTraceId, errors } = results;
				return {
					results: [...fetchResults],
					fetchEntitiesRequestId: fetchEntitiesRequestId ?? undefined,
					fetchEntitiesTraceId: fetchEntitiesTraceId ?? undefined,
					fetchSpacesRequestId: undefined as string | undefined,
					errors,
					autocompleteSuggestions: undefined,
				};
			});
		},
		[
			bootstrap.aggAbsoluteUrl,
			bootstrap.primaryProduct,
			bootstrap.cloudId,
			bootstrap.experimentConfig,
			bootstrap.productEdition,
			bootstrap.productDataRegions,
			bootstrap.thirdPartyConfigs,
			bootstrap.analyticsAttributes?.activationId,
			quickFindQuery,
			searchSessionId,
			selectedFilters,
			setRequestedBackendExperiments,
			sessionId,
			createNetworkCallbacks,
		],
	);

	const fetchJiraIssuesCallback = useCallback(
		async (uxTaskId: string, isPreQuery: boolean = false) => {
			const issuesAndTypesCallbacks = createNetworkCallbacks({
				taskName: TASK_NAMES.QUICK_FIND_JIRA_ISSUES_AND_TYPES_NETWORK,
				uxTaskId,
				isPreQuery,
			});
			const entitiesCallbacks = !hasFiltersSelected
				? createNetworkCallbacks({
						taskName: TASK_NAMES.QUICK_FIND_JIRA_ENTITIES_NETWORK,
						uxTaskId,
						isPreQuery,
					})
				: undefined;

			return Promise.all([
				fetchJiraIssuesAndTypeEntities(
					bootstrap.aggAbsoluteUrl,
					quickFindQuery,
					bootstrap.primaryProduct,
					bootstrap.cloudId || '',
					searchSessionId,
					selectedFilters,
					bootstrap.experimentConfig,
					bootstrap.productEdition,
					bootstrap.productDataRegions,
					setRequestedBackendExperiments,
					bootstrap.isJiraIssueIndexed,
					bootstrap.thirdPartyConfigs,
					typeFilterEntities,
					sessionId,
					bootstrap.hasEditOrViewPlanPermission,
					issuesAndTypesCallbacks,
					bootstrap.analyticsAttributes?.activationId,
				),
				!hasFiltersSelected
					? fetchJiraEntities(
							bootstrap.aggAbsoluteUrl,
							quickFindQuery,
							bootstrap.primaryProduct,
							bootstrap.cloudId || '',
							searchSessionId,
							selectedFilters,
							bootstrap.experimentConfig,
							bootstrap.productEdition,
							bootstrap.productDataRegions,
							setRequestedBackendExperiments,
							bootstrap.isJiraIssueIndexed,
							bootstrap.thirdPartyConfigs,
							sessionId,
							bootstrap.hasEditOrViewPlanPermission,
							entitiesCallbacks,
							bootstrap.analyticsAttributes?.activationId,
						)
					: Promise.resolve({ fetchResults: [], errors: [], autocompleteSuggestions: undefined }),
			]).then((results) => {
				const [issuesResult, entitiesResult] = results;

				return {
					results: [...issuesResult.fetchResults, ...entitiesResult.fetchResults],
					fetchEntitiesRequestId: issuesResult.fetchEntitiesRequestId ?? undefined,
					fetchEntitiesTraceId: issuesResult.fetchEntitiesTraceId ?? undefined,
					fetchSpacesRequestId: undefined,
					errors: [...(issuesResult.errors || []), ...(entitiesResult.errors || [])],
					autocompleteSuggestions: issuesResult.autocompleteSuggestions,
				};
			});
		},
		[
			bootstrap.aggAbsoluteUrl,
			bootstrap.primaryProduct,
			bootstrap.cloudId,
			bootstrap.experimentConfig,
			bootstrap.productEdition,
			bootstrap.productDataRegions,
			bootstrap.isJiraIssueIndexed,
			bootstrap.thirdPartyConfigs,
			bootstrap.hasEditOrViewPlanPermission,
			bootstrap.analyticsAttributes?.activationId,
			quickFindQuery,
			searchSessionId,
			selectedFilters,
			setRequestedBackendExperiments,
			typeFilterEntities,
			hasFiltersSelected,
			sessionId,
			createNetworkCallbacks,
		],
	);

	/**
	 * Callback to fetch people and teams results
	 */
	const fetchPeopleAndTeamsCallback = useCallback(async () => {
		const { orgId, primaryProduct, user, cloudId } = bootstrap;
		if (orgId && primaryProduct && user?.id && cloudId) {
			// When a single query filter is selected and it is a contributor filter,
			// get people and teams results using the contributor name
			if (selectedQueryFilters.length === 1 && selectedQueryFilters[0].filterId === 'contributor') {
				return fetchPeopleAndTeamsResults(
					selectedQueryFilters[0].displayText,
					orgId,
					primaryProduct,
					user.id,
					cloudId,
				);
			}

			// Only fetch people and teams results if there are no filters selected
			if (!hasFiltersSelected) {
				return fetchPeopleAndTeamsResults(quickFindQuery, orgId, primaryProduct, user.id, cloudId);
			}
		}

		return [];
	}, [bootstrap, quickFindQuery, selectedQueryFilters, hasFiltersSelected]);

	/**
	 * Function which fetches search results. This consists of a call to get
	 * product search results and a call to get people and teams. Given that Promise.allSettled
	 * is used, the slowest request will determine the time it takes to resolve.
	 *
	 * @param isWarmupRequest - Whether this is a warmup request (controls success/failure callbacks and which UX event to fire)
	 */
	const fetchQuickFindPostQuery = useCallback(
		(isWarmupRequest = false) => {
			// Determine if this is a pre-query state based on whether there's actual text in the query
			// isPreQuery = true when there's no text query (even if filters are selected)
			// isPreQuery = false when there's actual text in the query
			const isPreQuery = !quickFindQuery || quickFindQuery.length === 0;

			// Generate and store uxTaskId so network events use the same taskId as UX events.
			// For pre-query: use preQueryTaskIdRef, for post-query: use postQueryTaskState.
			let uxTaskId: string;
			if (isPreQuery) {
				if (preQueryTaskIdRef && !preQueryTaskIdRef.current) {
					preQueryTaskIdRef.current = crypto.randomUUID();
				}
				uxTaskId = preQueryTaskIdRef?.current || crypto.randomUUID();
			} else {
				// Reset state if previous query completed
				if (postQueryTaskState?.current?.completed) {
					postQueryTaskState.current = { id: null, started: false, completed: false, error: null };
				}
				if (postQueryTaskState && !postQueryTaskState.current.id) {
					postQueryTaskState.current.id = crypto.randomUUID();
				}
				uxTaskId = postQueryTaskState?.current?.id || crypto.randomUUID();
			}

			let fetchProductEntities;
			if (
				bootstrap.primaryProduct === PrimaryProductKeys.Confluence ||
				(bootstrap.primaryProduct === PrimaryProductKeys.RovoExtension &&
					fg('quickfind-rovo-extension'))
			) {
				fetchProductEntities = () => fetchConfluenceEntitiesCallback(uxTaskId, isPreQuery);
			} else if (bootstrap.primaryProduct === PrimaryProductKeys.Atlas) {
				fetchProductEntities = () => fetchAtlasEntitiesCallback(uxTaskId, isPreQuery);
			} else if (bootstrap.primaryProduct === PrimaryProductKeys.Focus) {
				fetchProductEntities = () => fetchMercuryEntitiesCallback(uxTaskId, isPreQuery);
			} else if (
				bootstrap.primaryProduct === PrimaryProductKeys.Talent &&
				fg('talent_global_search')
			) {
				fetchProductEntities = () => fetchTalentEntitiesCallback(uxTaskId, isPreQuery);
			} else if (bootstrap.primaryProduct === PrimaryProductKeys.Jira) {
				fetchProductEntities = () => fetchJiraIssuesCallback(uxTaskId, isPreQuery);
			} else if (
				bootstrap.primaryProduct === PrimaryProductKeys.Assets &&
				fg('assets_rovo_search_all')
			) {
				fetchProductEntities = () => fetchAssetsEntitiesCallback(uxTaskId, isPreQuery);
			} else if (bootstrap.primaryProduct === PrimaryProductKeys.Loom && fg('loom-rovo-search')) {
				fetchProductEntities = () => fetchLoomEntitiesCallback(uxTaskId, isPreQuery);
			} else {
				fetchProductEntities = () =>
					Promise.resolve({
						results: [],
						fetchEntitiesRequestId: undefined,
						fetchEntitiesTraceId: undefined,
						fetchSpacesRequestId: undefined,
						errors: [],
						autocompleteSuggestions: undefined,
					});
			}

			const fetchPeopleAndTeams =
				bootstrap.primaryProduct !== PrimaryProductKeys.Jira &&
				bootstrap.primaryProduct !== PrimaryProductKeys.Focus &&
				!selectedProduct
					? fetchPeopleAndTeamsCallback
					: () => Promise.resolve([]);

			const postQueryPromise = new CancellablePromise(
				Promise.allSettled([
					measureAsyncFunction(fetchProductEntities),
					measureAsyncFunction(fetchPeopleAndTeams),
				]),
			);
			postQueryPromiseRef.current = postQueryPromise;

			// Snapshot the generation at issue time so the resolver can drop stragglers.
			// Warmup requests never paint UI; control leaves it undefined (guard passes).
			const requestGeneration =
				instantSearch && !isWarmupRequest ? postQueryGenerationRef.current : undefined;

			// Only fire postquery events for actual queries, not warmup requests
			if (!isWarmupRequest) {
				onPostQueryStart?.();
			}

			postQueryPromise
				.promise()
				.then((results) => {
					// Drop this response if a newer request has already applied its results.
					if (
						!shouldApplyPostQueryResponse(
							requestGeneration,
							latestAppliedPostQueryGenerationRef.current,
						)
					) {
						return results;
					}

					if (requestGeneration !== undefined) {
						latestAppliedPostQueryGenerationRef.current = requestGeneration;
					}

					const productEntitiesStatus = results[0].status;
					const peopleAndTeamsStatus = results[1].status;

					// Post-query success is determined by product entities request, errors
					// from people and teams are ignored
					if (productEntitiesStatus === 'fulfilled') {
						const fetchedResults = results[0].value.result.results;
						setSearchResults({
							result: fetchedResults,
							duration: results[0].value.duration,
						});

						const errors = results[0].value.result.errors || [];
						setSearchErrors(errors);

						const hasThirdPartyResults = fetchedResults.some(
							(result) =>
								result.id.startsWith('ari:cloud:graph::document') ||
								result.id.startsWith('ari:cloud:graph::remote-link'),
						);

						setFetchEntitiesRequestId(results[0].value.result.fetchEntitiesRequestId);
						setFetchEntitiesTraceId(results[0].value.result.fetchEntitiesTraceId);
						setFetchSpacesRequestId(results[0].value.result.fetchSpacesRequestId);
						setAutocompleteSuggestions(results[0].value.result.autocompleteSuggestions);

						const peopleAndTeams =
							peopleAndTeamsStatus === 'fulfilled'
								? {
										result: results[1].value.result,
										duration: results[1].value.duration,
									}
								: EMPTY_RESULTS;
						setPeopleAndTeams(peopleAndTeams);

						const allDurations = results.map((result) =>
							result.status === 'fulfilled' ? result.value.duration : 0,
						);
						const maxDuration = Math.max(...allDurations);

						const resultCount = fetchedResults.length;

						// Only fire postquery events for actual queries, not warmup requests
						if (!isWarmupRequest) {
							onPostQuerySuccess?.(maxDuration, hasThirdPartyResults, resultCount);
						}
					} else {
						const error = new Error('Failed to fetch post query results');
						setPostQueryError(error);
						// Only fire postquery events for actual queries, not warmup requests
						if (!isWarmupRequest) {
							onPostQueryFailure?.(error);
						}
					}

					// Only clear loading when this is the latest issued request, so an older
					// completion can't hide the progress bar while a newer one is in flight.
					if (shouldClearPostQueryLoading(requestGeneration, postQueryGenerationRef.current)) {
						setPostQueryLoading(false);
					}
					return results;
				})
				.catch((error: Error) => {
					if (!(error instanceof CancelError)) {
						logException(error);
					}
				});
		},
		[
			bootstrap,
			fetchAssetsEntitiesCallback,
			fetchAtlasEntitiesCallback,
			fetchConfluenceEntitiesCallback,
			fetchJiraIssuesCallback,
			fetchLoomEntitiesCallback,
			fetchMercuryEntitiesCallback,
			fetchTalentEntitiesCallback,
			fetchPeopleAndTeamsCallback,
			onPostQueryFailure,
			onPostQueryStart,
			onPostQuerySuccess,
			selectedProduct,
			setFetchEntitiesRequestId,
			setFetchEntitiesTraceId,
			setFetchSpacesRequestId,
			setSearchErrors,
			logException,
			preQueryTaskIdRef,
			postQueryTaskState,
			quickFindQuery,
			instantSearch,
		],
	);

	/**
	 * Debounced fetch function for quick find results. This callback should never mutate
	 * as the only dependency is the timeout value, hence it can be safely used in dependency array.
	 */
	const debouncedFetchQuickFindPostQuery = useDebouncedCallback(
		fetchQuickFindPostQuery,
		debounceTimeout,
	);

	useEffect(() => {
		if (quickFindOpen) {
			// If the user is allocated to this mutually exclusive experiment,
			// We fire an exposure by accessing a parameter within the layer.
			getMutuallyExclusiveUIExperiment(
				'quick_find_user_relevant_documents_experiment',
				STATSIG_QUICK_FIND_LAYER,
			)?.get('cohort', undefined);

			serve3PClickIngestionExperiment(bootstrap.thirdPartyConfigs);
		}
	}, [quickFindOpen, bootstrap.thirdPartyConfigs, bootstrap.primaryProduct]);

	/**
	 * Updates results when various Quick Find states change, this includes:
	 * - when search is ready
	 * - when the dialog opens
	 * - when the query changes
	 * - when recent queries or activities change
	 * - when selected entities or filters change
	 */
	useEffect(() => {
		if (!isSearchReady) {
			return;
		}

		if (preQueryPromiseRef.current) {
			preQueryPromiseRef.current.cancel();
		}

		const hideFilteredPreQueryResults = !!(hasFiltersSelected || selectedProduct);

		const shouldRefetchPreQueryResults =
			prevQuickFindOpenRef.current === false && quickFindOpen === true;

		fetchQuickFindPreQuery(
			quickFindQuery,
			hideFilteredPreQueryResults,
			shouldRefetchPreQueryResults,
		);

		prevQuickFindOpenRef.current = quickFindOpen;

		/**
		 * Skip fetching post-query results if:
		 * - Quick Find is closed
		 * - Post-query is disabled
		 * - There is a current query filter selected
		 */
		if (!quickFindOpen || postQueryDisabled || currentQueryFilter) {
			return;
		}

		// Check if search parameters have changed, if not, skip fetching post-query results
		if (
			quickFindQuery === prevQueryRef.current &&
			selectedFiltersEquality === prevSelectedFiltersEquality.current &&
			entitiesEquality === prevEntitiesEquality.current &&
			prevHasFiltersSelected.current === hasFiltersSelected &&
			selectedProduct === prevSelectedProduct.current
		) {
			return;
		}

		prevQueryRef.current = quickFindQuery;
		prevSelectedFiltersEquality.current = selectedFiltersEquality;
		prevEntitiesEquality.current = entitiesEquality;
		prevHasFiltersSelected.current = hasFiltersSelected;
		prevSelectedProduct.current = selectedProduct;

		if (!instantSearch) {
			// Control: reset results (blank to skeletons) on query change.
			setSearchResults(EMPTY_RESULTS);
			setPeopleAndTeams(EMPTY_RESULTS);
		}
		// Treatment keeps the previous results in state for stale-while-revalidate.
		setPostQueryLoading(true);
		setPostQueryError(undefined);

		if (!instantSearch && postQueryPromiseRef.current) {
			// Control cancels in-flight on query change; treatment lets it resolve and
			// relies on the generation guard to decide which response paints.
			postQueryPromiseRef.current.cancel();
			onPostQueryCancel?.();
			postQueryPromiseRef.current = undefined;
		}

		const hasProductSelected = Boolean(selectedProduct) ?? false;
		const hasQuery = containsSearchParameters({
			query: quickFindQuery,
			hasFiltersSelected: hasFiltersSelected || hasProductSelected,
		});

		if (hasQuery) {
			// Bump the generation on the keystroke (before the debounce) so a straggler
			// resolving during the debounce window is treated as stale.
			if (instantSearch) {
				++postQueryGenerationRef.current;
			}
			setPostQueryLoading(true);
			debouncedFetchQuickFindPostQuery();
		} else if (instantSearch) {
			// Full clear: return to recents, so drop the kept results. Raising the applied
			// watermark to this new generation makes the guard reject any in-flight
			// straggler that would otherwise repaint stale results over the cleared state.
			++postQueryGenerationRef.current;
			latestAppliedPostQueryGenerationRef.current = postQueryGenerationRef.current;
			setPostQueryLoading(false);
			setSearchResults(EMPTY_RESULTS);
			setPeopleAndTeams(EMPTY_RESULTS);
			// Also drop the previous query's highlighted pre-query collections. Unlike the
			// control path they are not refetched-and-replaced synchronously on clear, so
			// without this a highlighted recent/recommended row lingers on the landing view.
			// The subsequent pre-query fetch repopulates them unhighlighted.
			clearFilteredPreQueryResults();
		}
	}, [
		fetchQuickFindPreQuery,
		debouncedFetchQuickFindPostQuery,
		quickFindQuery,
		retryCount,
		isSearchReady,
		quickFindOpen,
		selectedFilters,
		entities,
		onPostQueryCancel,
		postQueryDisabled,
		currentQueryFilter,
		selectedProduct,
		hasFiltersSelected,
		selectedFiltersEquality,
		entitiesEquality,
		clearFilteredPreQueryResults,
		instantSearch,
	]);
	return useMemo(
		() =>
			getQuickFindContent({
				// Using refs here to ensure new results state is returned only
				// when the various result variables in the dependency array change
				query: prevQueryRef.current,
				hasFiltersSelected: prevHasFiltersSelected.current,
				quickFindOpen: prevQuickFindOpenRef.current,
				preQueryError,
				preQueryLoading,
				postQueryError,
				postQueryLoading,
				filteredRecentQueries,
				filteredRecentActivities,
				filteredRecommendedDocuments,
				filteredZeroQueries,
				searchResults,
				peopleAndTeams,
				postQueryDisabled,
				primaryProduct: bootstrap.primaryProduct,
				hasProductSelected: !!prevSelectedProduct.current,
				autocompleteSuggestions,
				instantSearch,
			}),
		[
			filteredRecentActivities,
			filteredRecommendedDocuments,
			filteredRecentQueries,
			filteredZeroQueries,
			peopleAndTeams,
			postQueryDisabled,
			postQueryError,
			postQueryLoading,
			preQueryError,
			preQueryLoading,
			searchResults,
			bootstrap.primaryProduct,
			autocompleteSuggestions,
			instantSearch,
		],
	);
};

// export for testing purposes
export const useSearchResultsShownAnalytics: () => void = (): void => {
	const previousStateRef = useRef<State | undefined>();

	const content = useContent();
	const quickFindOpen = useQuickFindOpen();
	const [{ thirdPartyConfigs }] = useBootstrap();
	const searchErrors = useSearchErrors();

	const { fireAnalyticsEvent } = useQuickFindAnalytics();
	const { commonAttributes } = useQuickFindAttributes();
	// eslint-disable-next-line @atlassian/search-common/no-use-rss-hooks
	const [requestedBackendExperiments] = useRequestedBackendExperiments();

	const intl = useIntl();
	const [primaryProduct] = usePrimaryProduct();
	const intentDetectionExperiment = serveJiraNINFpsNLQNavigation({
		primaryProduct,
		fireLayerExposure: false, // Exposure is fired when a user calls the intent detection API
		locale: intl.locale,
	});
	const intentRouterEnabled = intentDetectionExperiment?.cohort === 'test';
	const intentDetectionCohort = intentDetectionExperiment?.cohort;

	const productsWithTimeoutsFromErrors: ProductKeys[] | undefined = useMemo(() => {
		const errors = searchErrors || [];

		return errors
			.filter((error: AggError) => error.extensions?.statusCode === 408)
			.map((error: AggError) => error.extensions?.integration ?? error.extensions?.product)
			.flatMap((s: Maybe<string> | undefined) => (s && isProductKey(s) ? [s] : []));
	}, [searchErrors]);

	const are3PSmartLinksInResults = useMemo(() => {
		if (!content) {
			return false;
		}

		const checkIsSmartLink = (result: QuickFindResult<ActivityItemEdge | SearchPageNode>) => {
			if (!result.data?.node?.object?.id || !result.data?.node?.event?.eventType) {
				return false;
			}

			const mappedResult = mapActivityToSearchResult(
				result.data,
				'', // query not needed to obtain the `product` value
				'', // searchSessionId not needed to obtain the `product` value
				thirdPartyConfigs,
			);

			return isResultSmartLink({
				resultAri: result.data.node.object.id,
				thirdPartyConfigs,
				productKey: mappedResult?.analyticsMetadata.product,
				eventType: result.data.node.event.eventType,
				connectorType: result.data.connectorType,
			});
		};

		return (
			('recentActivities' in content ? content.recentActivities : []).some(checkIsSmartLink) ||
			('searchResults' in content ? content.searchResults : []).some(checkIsSmartLink)
		);
	}, [content, thirdPartyConfigs]);

	useEffect(() => {
		// Store a copy of previous state before updating the ref
		const previousState = previousStateRef.current;
		previousStateRef.current = content ? content.state : undefined;

		// Don't fire analytics event if the dialog is closed
		if (!quickFindOpen || !content) {
			return;
		}

		// Don't fire analytics event if the state hasn't changed
		if (content.state === previousState) {
			return;
		}

		// Ignore states that do not have search results
		if (
			content.state === State.PREQUERY_LOADING ||
			content.state === State.PREQUERY_ERROR ||
			content.state === State.POSTQUERY_ERROR
		) {
			if (content.state === State.PREQUERY_ERROR) {
				fireAnalyticsEvent(
					onQuickFindOperational({
						success: false,
						attributes: commonAttributes,
						resultsType: 'preQuerySearchResults',
					}),
				);
			}
			if (content.state === State.POSTQUERY_ERROR) {
				fireAnalyticsEvent(
					onQuickFindOperational({
						success: false,
						attributes: commonAttributes,
						resultsType: 'postQuerySearchResults',
					}),
				);
			}
			return;
		}

		/**
		 * Do not fire analytics event when the dialog is opened and post-query
		 * results are already returned. This is to have parity with the old
		 * quick find component.
		 *
		 * Whilst this may not be correct, this is here to ensure changes in metrics
		 * such as QSR and CTR are intentional.
		 *
		 * TODO: Discuss with DS/DE if this correct behaviour
		 */
		if (content.state === State.POSTQUERY_RESULTS && previousState === undefined) {
			return;
		}

		const resultsTypeMap = {
			PREQUERY_RESULTS: 'preQuerySearchResults' as const,
			PREQUERY_NO_RESULTS: 'preQuerySearchResults' as const,
			POSTQUERY_LOADING: 'cachedResults' as const,
			POSTQUERY_RESULTS: 'postQuerySearchResults' as const,
			POSTQUERY_NO_RESULTS: 'postQuerySearchResults' as const,
		};

		const resultsType = resultsTypeMap[content.state];

		const { sectionCount, resultCount } = getSectionsAndResultsCount();
		const resultTypeCounts = getResultTypeCounts();
		const productsInResults = getProductsInResults();
		const isBulkConnectBannerShown = getIsBulkConnectBannerShown();
		const resultsShown = getResultsShown();
		const noResultsFetched =
			content.state === State.PREQUERY_NO_RESULTS ||
			content.state === State.POSTQUERY_NO_RESULTS ||
			resultCount === 0;

		// Don't fire cachedResults event if there are no results
		if (noResultsFetched && resultsType === 'cachedResults') {
			return;
		}

		const are3pResultsShown = productsInResults.some(is3pProductKeyForAnalytics);
		const are3PInRecommendations = getProductsInRecommendations().some(is3pProductKeyForAnalytics);

		const authorized3PProducts = Object.keys(thirdPartyConfigs || {}).filter(
			(product) => !thirdPartyConfigs?.[product as ProductKeys3PType]?.userNeedsOAuth,
		);

		fireAnalyticsEvent(
			onSearchResultsShown({
				attributes: {
					...commonAttributes,
					sectionCount,
					resultCount,
					resultTypeCounts,
					productsInResults,
					are3pResultsShown,
					// Use `searchAndResult` to keep the schema consistent with FPS, but we don't need other attributes yet.
					searchAndResult: {
						authorized3PProducts,
					},
					resultsShown,
					isBulkConnectBannerShown,
					are3PSmartLinksInResults,
					noResultsFetched,
					...getCommonQueryAttributes(content.query),
					...requestedBackendExperiments,
					...(resultsType === 'postQuerySearchResults'
						? { productsWithTimeout: productsWithTimeoutsFromErrors || [] }
						: {}),
					are3PInRecommendations,
					intentDetectionTriggered: isIntentDetectionTriggeredForExperiment(
						primaryProduct,
						intl.locale,
					),
					queryClassification: INTENT_DETECTION.value()?.mode,
					modelId: INTENT_DETECTION.value()?.modelId,
					cohort: intentDetectionCohort,
					patternType: INTENT_DETECTION.value()?.patternType,
				},
				nonPrivacySafeAttributes: {
					query: content.query,
				},
				resultsType,
			}),
		);

		fireAnalyticsEvent(
			onQuickFindOperational({
				success: true,
				attributes: commonAttributes,
				resultsType,
			}),
		);
	}, [
		commonAttributes,
		content,
		fireAnalyticsEvent,
		quickFindOpen,
		requestedBackendExperiments,
		are3PSmartLinksInResults,
		thirdPartyConfigs,
		productsWithTimeoutsFromErrors,
		intentRouterEnabled,
		primaryProduct,
		intl.locale,
		intentDetectionCohort,
	]);
};

// export for testing purposes
export const useSuggestedQueriesShownAnalytics: () => void = (): void => {
	const previousStateRef = useRef<State | undefined>();

	const content = useContent();
	const quickFindOpen = useQuickFindOpen();

	const { fireAnalyticsEvent } = useQuickFindAnalytics();
	const { commonAttributes, nonPrivacySafeAttributes } = useQuickFindAttributes();

	const intl = useIntl();
	const [primaryProduct] = usePrimaryProduct();
	const intentRouterEnabled =
		serveJiraNINFpsNLQNavigation({
			primaryProduct,
			fireLayerExposure: false, // Exposure is fired when a user calls the intent detection API
			locale: intl.locale,
		})?.cohort === 'test';

	useEffect(() => {
		// Store a copy of previous state before updating the ref
		const previousState = previousStateRef.current;
		previousStateRef.current = content ? content.state : undefined;

		// Don't fire analytics event if the dialog is closed
		if (!quickFindOpen || !content) {
			return;
		}

		// Don't fire analytics event if the state hasn't changed
		if (content.state === previousState) {
			return;
		}

		// Fire a single consolidated event with all suggested queries when Quick Find opens.
		// This complements (but does not replace) the existing per-item suggestedQuery shown events.
		// Read actually-rendered zero query nodes from the DOM (mirrors getResultsShown approach),
		// so the event only reflects what was visible to the user.
		const shownZeroQueries = getShownZeroQueries();
		if (shownZeroQueries.length > 0) {
			fireAnalyticsEvent(
				onAllSuggestedQueriesShown({
					actionSubjectId: getActionSubjectId(content.state),
					attributes: {
						// All existing commonAttributes — same as what each per-item suggestedQuery shown event sends
						...commonAttributes,
						// Intent router attributes (same gate as existing per-item event)
						...(intentRouterEnabled
							? {
									queryClassification: INTENT_DETECTION.value()?.mode,
									modelId: INTENT_DETECTION.value()?.modelId,
									patternType: INTENT_DETECTION.value()?.patternType,
								}
							: {}),
						// Per-item fields consolidated into one array, derived from DOM
						suggestedQueriesShown: shownZeroQueries.map(({ zeroQueryType, zeroQueryPosition }) => ({
							zeroQueryType,
							zeroQueryPosition,
							zeroQueryVersion: getZeroQueryVersion(zeroQueryType as ZeroQueryType),
							is3PZeroQuery: is3PZeroQuery(zeroQueryType as ZeroQueryType),
						})),
					},
					// Same nonPrivacySafeAttributes as the existing per-item event
					nonPrivacySafeAttributes,
				}),
			);
		}
	}, [
		commonAttributes,
		nonPrivacySafeAttributes,
		content,
		fireAnalyticsEvent,
		quickFindOpen,
		intentRouterEnabled,
	]);
};

export const QuickFindResults: import('react').MemoExoticComponent<
	(options: UseQuickFindResultsOptions) => null
> = memo((options: UseQuickFindResultsOptions) => {
	const callbacks = useQuickFindResultsCallbacks();

	const useQuickFindResultsArgs = useMemo(
		() => ({ ...options, ...callbacks }),
		[options, callbacks],
	);

	const content = useQuickFindResults(useQuickFindResultsArgs);
	const { setContent } = useQuickFindActions();

	useEffect(() => {
		setContent(content);
	}, [content, setContent]);

	useSearchResultsShownAnalytics();
	useSuggestedQueriesShownAnalytics();

	return null;
});
